import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import TextInputComponent from '@components/textInput/TextInput'
import SortSelector from '@components/modal/SortSelector'
import { RadioOption } from '@components/radio/Radio'

import UserCard from '@components/card/UserCard'

import { filterUsers } from '@utils/admin'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'
import * as AuthStore from '@store/auth/auth'
import { AuthScope } from '@api/enum'

import api from '@api/api'
import Logger from '@utils/logger'
import Button from '@components/button/Button'

interface Props {
  manifestationId: string
}

const MAIN_VERTICAL_PADDING = 24

const AdminManifScreen = ({ manifestationId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const manifestations = useReducer(AdminStore.store, (s) => s.manifestations)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const canAdd =
    scopes.includes(AuthScope.AdminUtilisateursCreationTous) ||
    scopes.includes(AuthScope.AdminUtilisateursCreationOrganisateur)

  const manifestation = React.useMemo(
    () => manifestations.find((manif) => manif.id === manifestationId),
    [manifestations, manifestationId]
  )

  const [users, setUsers] = React.useState<UserResponse[]>([])

  const [status, setStatus] = React.useState<Status>('loading')
  const [search, setSearch] = React.useState('')

  const sorts: RadioOption[] = [{ label: i18n.t('screens.admin.users.sorts.name'), value: 'name' }]

  const [reverse, setReverse] = React.useState(false)
  const [sort, setSort] = React.useState<RadioOption | undefined>(sorts[0])

  React.useEffect(() => {
    if (!manifestationId) {
      setStatus('error')
    } else {
      Promise.all([api.users.all(undefined, manifestationId), api.manifestation.all()])
        .then(([userRes, manifRes]) => {
          setUsers(userRes)
          AdminStore.actions.setManifestations(manifRes)
          setStatus('fetched')
        })
        .catch((err) => {
          Logger.error(err)
          setStatus('error')
        })
    }
  }, [manifestationId])

  const displayedData = React.useMemo(() => {
    const filtered = filterUsers(users, search)

    const sorted = sort
      ? [...filtered].sort(
          (a, b) => (sort.value === 'name' ? a.prenom.localeCompare(b.prenom) : 1) * (reverse ? -1 : 1)
        )
      : filtered

    return sorted
  }, [users, sort, reverse, search])

  const addUserButton: HeaderButton | undefined = canAdd
    ? {
        label: i18n.t('screens.admin.users.buttons.addUser'),
        onClick: () => navigation.push(`/admin/users/form/info?manifestationId=${manifestationId}`),
      }
    : undefined

  const headerLinks: HeaderLink[] = [
    { label: i18n.t('screens.admin.organizers.title'), onClick: () => navigation.push('/admin/organizers') },
  ]

  const renderUserCard = ({ item }: { item: UserResponse }) => (
    <UserContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/users/${item.id}`, { userId: item.id })}>
      <UserCard user={item} />
    </UserContainer>
  )

  const renderSearch = () => (
    <SearchContainer>
      <TextInputComponent
        onChange={setSearch}
        searchStyle="onDark"
        placeholder={i18n.t('screens.incidentList.search')}
        left={{ name: 'search' }}
        value={search}
      />
    </SearchContainer>
  )

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? manifestation?.libelle || i18n.t('screens.admin.organizers.title') : undefined}
      renderSubHeader={isPhone ? renderSearch : undefined}
      fixed={isPhone}
      onLeft={navigation.goBack}>
      {!!manifestation && status === 'fetched' ? (
        <>
          {!isPhone && (
            <HeaderContainer>
              <Header
                title={manifestation.libelle}
                renderBottom={renderSearch}
                buttons={!!addUserButton ? [addUserButton] : []}
                links={headerLinks}
              />
            </HeaderContainer>
          )}
          <SortContainer>
            <SortSelector
              onOrderChange={() => setReverse(!reverse)}
              onSortChange={setSort}
              reverse={reverse}
              sorts={sorts}
              sort={sort}
            />
          </SortContainer>
          <FlatList
            data={displayedData}
            renderItem={renderUserCard}
            contentContainerStyle={{
              flexWrap: isPhone ? undefined : 'wrap',
              flexDirection: isPhone ? 'column' : 'row',
              marginVertical: 20,
              marginHorizontal: isPhone ? 16 : 25,
              justifyContent: isPhone ? 'center' : 'flex-start',
            }}
            ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.users.empty')}</MissingInfo>}
            showsVerticalScrollIndicator={false}
          />
          {isPhone && !!addUserButton && (
            <BottomContainer>
              <Button status="active" label={addUserButton.label} onClick={addUserButton.onClick} />
            </BottomContainer>
          )}
        </>
      ) : (
        <MainContainer>
          <MissingInfo>{i18n.t('common.error')}</MissingInfo>
        </MainContainer>
      )}
    </Root>
  )
}

export default AdminManifScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const UserContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SortContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint !== 'phone' ? `16px 0 0 24px` : `10px 16px`)};
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`

const MainContainer = styled(View)`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`
const BottomContainer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`
